import { segmentColorAtIndex } from '@/components/planning/StackedAllocationBar';
import { Slider } from '@/components/ui/slider';
import { twoWaySplit } from '@/features/planning/percentAllocation';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

interface DualPercentSplitSliderProps {
  leftId?: string;
  rightId?: string;
  leftLabel: string;
  rightLabel: string;
  leftPercent: number;
  leftValueLabel?: ReactNode;
  rightValueLabel?: ReactNode;
  onChange: (leftPercent: number, rightPercent: number) => void;
  onRemove?: (id: string) => void;
  removeLabel?: string;
}

export function DualPercentSplitSlider({
  leftId = 'left',
  rightId = 'right',
  leftLabel,
  rightLabel,
  leftPercent,
  leftValueLabel,
  rightValueLabel,
  onChange,
  onRemove,
  removeLabel,
}: DualPercentSplitSliderProps) {
  const leftColor = segmentColorAtIndex(0);
  const rightColor = segmentColorAtIndex(1);
  const splitValue = leftPercent;
  const rightPercent = 100 - splitValue;

  const rows = [
    {
      id: leftId,
      label: leftLabel,
      percent: splitValue,
      color: leftColor,
      valueLabel: leftValueLabel,
    },
    {
      id: rightId,
      label: rightLabel,
      percent: rightPercent,
      color: rightColor,
      valueLabel: rightValueLabel,
    },
  ];

  return (
    <div className='space-y-3'>
      <div className='relative flex w-full items-center py-1'>
        <div
          className='pointer-events-none absolute inset-x-0 flex h-2 overflow-hidden rounded-full'
          aria-hidden
        >
          <div className={cn('h-full', leftColor)} style={{ width: `${splitValue}%` }} />
          <div className={cn('h-full', rightColor)} style={{ width: `${rightPercent}%` }} />
        </div>
        <Slider
          value={[splitValue]}
          min={0}
          max={100}
          step={1}
          className='relative z-10 [&>span:first-child]:bg-transparent [&>span:first-child>span]:bg-transparent'
          onValueChange={([value]) => {
            const [left, right] = twoWaySplit(value);
            onChange(left, right);
          }}
          aria-label={`${leftLabel} / ${rightLabel}`}
        />
      </div>
      <ul className='flex flex-col gap-1'>
        {rows.map((row) => (
          <li key={row.id}>
            <div className='flex w-full min-w-0 items-center gap-1 px-2 py-1.5 pr-1 text-sm'>
              <div className='flex min-w-0 flex-1 items-center gap-2'>
                <span className={cn('size-2.5 shrink-0 rounded-sm', row.color)} aria-hidden />
                <span className='min-w-0 flex-1 truncate'>{row.label}</span>
                {row.valueLabel ? (
                  <span className='text-muted-foreground shrink-0 whitespace-nowrap tabular-nums'>
                    {row.valueLabel}
                  </span>
                ) : null}
                <span className='text-fin-white w-12 shrink-0 text-right font-medium whitespace-nowrap tabular-nums'>
                  {row.percent.toFixed(1)}%
                </span>
              </div>
              {onRemove ? (
                <button
                  type='button'
                  className='text-muted-foreground hover:text-fin-white shrink-0 rounded-md p-1'
                  aria-label={removeLabel}
                  onClick={() => onRemove(row.id)}
                >
                  <X className='size-3.5' aria-hidden />
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
